import {useLingui} from '@lingui/react/macro';
import {useCallback} from 'react';
import {useWindowDimensions, View, Text, Image, Pressable, StyleSheet} from 'react-native';
import {Svg, Defs, LinearGradient, Stop, Rect, Pattern, Circle} from 'react-native-svg';
import {useModal} from 'extensions/viewport/useModal';
import {JotaryWordmark} from 'interface/base/JotaryLogo';
import {openPage} from 'utils/mediafire';
import images from 'config/images';

const JOTARY_URL = 'https://jotary.com/';

// Aspect ratio of the bundled screenshot (jotary-paste.png is 1740x1160).
const PASTE_RATIO = 1160 / 1740;
const CARD_MAX_WIDTH = 560;
const HERO_PADDING = 40;

function JotaryModal({exit}: {exit: () => void}) {
  const {t} = useLingui();
  const screen = useWindowDimensions();

  const cardWidth = Math.min(CARD_MAX_WIDTH, screen.width - 32);
  const imageWidth = cardWidth - HERO_PADDING * 2;
  const imageHeight = imageWidth * PASTE_RATIO;
  const heroHeight = imageHeight + HERO_PADDING * 2;

  const tryJotary = useCallback(() => {
    // External site — skip the MediaFire login-token redirect.
    openPage(JOTARY_URL, true);
    exit();
  }, [exit]);

  return (
    <View style={styles.overlay}>
      <View style={[styles.card, {width: cardWidth}]}>
        {/* Gradient hero with a dotted overlay, drawn with react-native-svg
            so it renders the same on web and native. */}
        <View style={[styles.hero, {height: heroHeight, padding: HERO_PADDING}]}>
          <Svg width={cardWidth} height={heroHeight} style={StyleSheet.absoluteFill}>
            <Defs>
              <LinearGradient id="jotaryGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <Stop offset="0%" stopColor="#3730a3"/>
                <Stop offset="38%" stopColor="#4338ca"/>
                <Stop offset="72%" stopColor="#06b6d4"/>
                <Stop offset="100%" stopColor="#fc7950"/>
              </LinearGradient>
              <Pattern id="jotaryDots" patternUnits="userSpaceOnUse" width="10" height="10">
                <Circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.15)"/>
              </Pattern>
            </Defs>
            <Rect x="0" y="0" width={cardWidth} height={heroHeight} fill="url(#jotaryGrad)"/>
            <Rect x="0" y="0" width={cardWidth} height={heroHeight} fill="url(#jotaryDots)"/>
          </Svg>
          <Image
            source={images.jotaryPaste}
            accessibilityLabel="Jotary"
            resizeMode="contain"
            style={[styles.shot, {width: imageWidth, height: imageHeight}]}
          />
        </View>

        <View style={styles.body}>
          <View style={styles.logo}>
            <JotaryWordmark height={22}/>
          </View>
          <Text style={styles.title}>
            {t`A simple paste tool built for you and your agent`}
          </Text>
          <Text style={styles.text}>
            {t`Share data, pass context, and hand off outputs between tools. No auth required. Just a URL.`}
          </Text>
          <View style={styles.buttons}>
            <Pressable
              onPress={tryJotary}
              accessibilityRole="button"
              style={({hovered}: any) => [styles.tryButton, hovered && styles.tryButtonHover]}>
              <Text style={styles.tryText}>{t`Try Jotary`.toUpperCase()}</Text>
            </Pressable>
            <Pressable
              onPress={exit}
              accessibilityRole="button"
              style={({hovered}: any) => [styles.closeButton, hovered && styles.closeButtonHover]}>
              <Text style={styles.closeText}>{t`Close`}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

export function useModalJotary() {
  const modal = useModal();
  const exit = useCallback(() => modal.exit(), []);
  const open = useCallback(() => {
    modal.open(<JotaryModal exit={exit}/>, {frameless: true, overlay: true});
  }, [exit]);
  return {open, exit};
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  hero: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shot: {
    borderRadius: 12,
    opacity: 0.92,
  },
  body: {
    paddingHorizontal: 40,
    paddingTop: 28,
    paddingBottom: 40,
  },
  logo: {
    marginBottom: 14,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 9,
    lineHeight: 24,
  },
  text: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 23,
    marginBottom: 22,
  },
  buttons: {
    flexDirection: 'row',
    gap: 10,
  },
  tryButton: {
    flex: 1,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EC6A37',
    borderRadius: 6,
  },
  tryButtonHover: {
    backgroundColor: '#d45e2e',
  },
  tryText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.65,
  },
  closeButton: {
    height: 40,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 6,
  },
  closeButtonHover: {
    backgroundColor: '#f9fafb',
    borderColor: '#d1d5db',
  },
  closeText: {
    color: '#6b7280',
    fontSize: 13,
    fontWeight: '600',
  },
});
